import { classNames, transformDateToLocalDateString } from "helpers";
import { FleetVehicleRow } from "services/api/fleet/vehicles/types";
import { isNewVehicle } from "services/api/fleet/vehicles/consts";
import { CopyButton } from "ui/components/Button/CopyButton";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import Tooltip from "ui/components/Tooltip/Tooltip";
import Badge from "ui/components/Badge/Badge";
import { BadgeProps } from "ui/components/Badge/types";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const FUEL_TYPE_VARIANT: Record<string, BadgeVariant> = {
  B: "warning",
  P: "warning",
  D: "info",
  E: "success",
  H: "success-dark",
  LPG: "danger",
  CNG: "danger",
};

const TRANSMISSION_VARIANT: Record<string, BadgeVariant> = {
  A: "info",
  M: "warning",
  S: "success",
};

const pickVariant = (map: Record<string, BadgeVariant>, code: string | null | undefined, fallback: BadgeVariant): BadgeVariant =>
  (code && map[code.toUpperCase()]) || fallback;

/**
 * Stupci izvoza — isti dizajn kao pretraga vozila (bez "akcije" i cijena kolona).
 */
export const FLEET_VEHICLE_EXPORT_COLUMNS = (
  t: (key: string) => string
): Column[] => [
    {
      title: "",
      key: "image",
      field: "image",
      alwaysVisible: true,
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => {
        const fullName = [
          row.vehicleManufacturer?.name,
          row.vehicleModel?.name,
          row.vehicleType?.name,
        ].filter(Boolean).join(" ");
        const trigger = (
          <div className="cursor-help">
            <TableIcon
              imageUrl={row.picture}
              initials={row.vehicleManufacturer?.name?.toString().charAt(0).toUpperCase()}
              imageFit="cover"
            />
          </div>
        );
        return (
          <div
            className={classNames(
              "after:absolute after:-left-0.5 after:-top-0.5 after:bottom-0 after:w-[3px] after:content-['']",
              isNewVehicle(row.kmTraveled, row.firstRegistrationDate)
                ? "after:bg-green-400"
                : "after:bg-red-400"
            )}
          >
            <Tooltip position="top-right" size="medium" trigger={trigger}>
              <div className="min-w-[280px]">
                {row.picture && (
                  <div className="mb-3 rounded overflow-hidden">
                    <img src={row.picture} alt={fullName} className="w-full h-[120px] object-cover rounded" />
                  </div>
                )}
                <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Naziv</span>
                  <span className="font-medium text-right max-w-[180px] truncate">{fullName || "-"}</span>
                </div>
                {row.vinNumber && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">VIN</span>
                    <span className="font-medium text-xs">{row.vinNumber}</span>
                  </div>
                )}
                {row.registrationPlate && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Registracija</span>
                    <span className="font-medium">{row.registrationPlate}</span>
                  </div>
                )}
                {row.firstRegistrationDate != null && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Prva registracija</span>
                    <span className="font-medium">{transformDateToLocalDateString(row.firstRegistrationDate)}</span>
                  </div>
                )}
                {row.colorName && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Boja</span>
                    <span className="font-medium">{row.colorName}</span>
                  </div>
                )}
                {(row.vehicleType?.maximumPowerKw != null || row.vehicleType?.maximumPowerHp != null) && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Snaga</span>
                    <span className="font-medium">{row.vehicleType?.maximumPowerKw ?? "-"} kW / {row.vehicleType?.maximumPowerHp ?? "-"} KS</span>
                  </div>
                )}
                {row.vehicleType?.cylinderCapacity && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Zapremina</span>
                    <span className="font-medium">{row.vehicleType.cylinderCapacity} cm³</span>
                  </div>
                )}
                {row.vehicleType?.bodyCode && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Karoserija</span>
                    <span className="font-medium">{row.vehicleType.bodyCode}</span>
                  </div>
                )}
                {row.vehicleType?.numberOfDoors && row.vehicleType?.seats && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Konfiguracija</span>
                    <span className="font-medium">{row.vehicleType.numberOfDoors} vrata / {row.vehicleType.seats} sjedala</span>
                  </div>
                )}
                {row.vehicleType?.manufacturerCode && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Šifra proizvođača</span>
                    <span className="font-medium">{row.vehicleType.manufacturerCode}</span>
                  </div>
                )}
                {row.kmTraveled != null && row.kmTraveled > 0 && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Kilometraža</span>
                    <span className="font-medium">{row.kmTraveled.toLocaleString("hr-HR")} km</span>
                  </div>
                )}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.manufacturer",
      key: "manufacturer",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => {
        return (
          <div className="flex items-center gap-2">
            {row.vehicleManufacturer && (
              <img src={row.vehicleManufacturer?.image ?? ""} alt="" className="w-12 flex-shrink-0 rounded-full object-cover" />
            )}
            <div>
              {row.vehicleManufacturer?.name}
            </div>
          </div>);
      },
    },
    {
      title: "admin.fleet.vehicle.search.model",
      key: "model",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => {
        const yearRange = row.vehicleModel?.yearFrom && row.vehicleModel?.yearTo
          ? `${row.vehicleModel.yearFrom} – ${row.vehicleModel.yearTo}`
          : row.vehicleModel?.yearFrom
            ? `${row.vehicleModel.yearFrom} –`
            : row.vehicleModel?.year
              ? `${row.vehicleModel.year}`
              : null;
        const manufacturerCode = row.vehicleModel?.manufacturerCode;
        return (
          <div>
            <div className="text-md mb-1">
              {row.vehicleModel?.name}
              {manufacturerCode ? ` (${manufacturerCode})` : ""}
            </div>
            {yearRange && (
              <div className="text-xs text-slate-500">
                {t("admin.fleet.vehicle.search.vehiclemodel.year")}: {yearRange}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.type",
      key: "type",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) => {
        const showEngine = row.vehicleType?.engine && row.vehicleType.engine !== row.vehicleType.name;
        const parts = [
          row.vehicleType?.maximumPowerKw != null || row.vehicleType?.maximumPowerHp != null
            ? `${row.vehicleType?.maximumPowerKw}kW / ${row.vehicleType?.maximumPowerHp} KS`
            : null,
          row.vehicleType?.drivenWheels?.name ?? null,
        ].filter(Boolean);
        return (
          <div>
            <div className="text-md mb-0.5">
              {row.vehicleType?.name}
              {showEngine ? ` ${row.vehicleType?.engine}` : ""}
            </div>
            {parts.length > 0 && (
              <div className="text-xs text-slate-500">{parts.join(" · ")}</div>
            )}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.fueltype",
      key: "fuelType",
      field: "",
      alwaysVisible: false,
      defaultVisible: false,
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) =>
        row.vehicleType?.fuelType ? (
          <Badge
            text={row.vehicleType.fuelType.name ?? ""}
            variant={pickVariant(FUEL_TYPE_VARIANT, row.vehicleType.fuelType.id, "warning")}
            height="small"
            showDot
          />
        ) : null,
    },
    {
      title: "admin.fleet.vehicle.search.transmission",
      key: "transmission",
      field: "",
      alwaysVisible: false,
      defaultVisible: false,
      type: ColumnTypes.custom,
      render: (row: FleetVehicleRow) =>
        row.vehicleType?.transmission ? (
          <Badge
            text={row.vehicleType.transmission.name ?? ""}
            variant={pickVariant(TRANSMISSION_VARIANT, row.vehicleType.transmission.id, "info")}
            height="small"
            showDot
          />
        ) : null,
    },
    {
      title: "admin.fleet.vehicle.search.registrationplate",
      key: "name",
      field: "",
      alwaysVisible: true,
      type: ColumnTypes.custom,
      render: (row) => {
        return (
          <div className="">
            {row.registrationPlate && (
              <div className="font-medium">
                {row.registrationPlate}
                <CopyButton value={row.registrationPlate} />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.vinnumber",
      key: "name",
      field: "",
      alwaysVisible: true,
      type: ColumnTypes.custom,
      render: (row) => {
        return (
          <div className="">
            {row.vinNumber && (
              <div className="font-medium">
                {row.vinNumber}
                <CopyButton value={row.vinNumber} />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.currentkm",
      key: "currentKm",
      field: "",
      alwaysVisible: false,
      defaultVisible: false,
      type: ColumnTypes.custom,
      render: (row) => {
        return (
          <div className="text-md">
            {row.kmTraveled
              ? `${row.kmTraveled.toLocaleString("hr-Hr")} km`
              : "-"}
          </div>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.search.firstregistration",
      key: "firstRegistration",
      field: "",
      alwaysVisible: false,
      defaultVisible: false,
      type: ColumnTypes.custom,

      render: (row) => {
        return (
          <div className="text-md">
            {row.firstRegistrationDate
              ? `${transformDateToLocalDateString(row.firstRegistrationDate)}`
              : "-"}
          </div>
        );
      },
    },
  ];
